import { Capacitor } from '@capacitor/core';
import type { CardDeck } from '@shared/schema';

/**
 * Helper function to format card image URLs for both web and mobile environments
 * @param deckUrl - The base URL for the deck's assets
 * @param cardCode - The specific card code (e.g., "B13" for King of Hearts)
 * @returns Properly formatted URL for the card image, considering the platform environment
 */
export const getCardImageUrl = (deckUrl: string, cardCode: string): string => {
  let imageUrl = `${deckUrl}/${cardCode}.svg`;
  
  if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
    if (deckUrl.startsWith('/')) {
      imageUrl = `${import.meta.env.VITE_SERVER_URL}${imageUrl}`;
    }
  }
  
  return imageUrl;
};

/**
 * Helper function to determine if a deck matches the selected tab
 * @param deck - The deck to check
 * @param tab - The currently selected tab ('all', 'free', or 'premium')
 * @returns boolean indicating if the deck should be shown in the current tab
 */
export const deckMatchesTab = (deck: CardDeck, tab: string): boolean => {
  switch (tab) {
    case "free":
      return deck.membership === "free";
    case "premium":
      return deck.membership === "premium" || deck.membership === "exclusive";
    case "all":
    default:
      return true;
  }
};

/**
 * Filter decks based on search query, type, membership, and active tab
 */
export const filterDecks = (decks: CardDeck[] | undefined, {
  searchQuery,
  selectedType,
  selectedFilter,
  activeTab
}: {
  searchQuery: string,
  selectedType: string,
  selectedFilter: string,
  activeTab: string
}) => {
  return decks?.filter((deck: CardDeck) => {
    const matchesSearch = searchQuery
      ? deck.name.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    const matchesType = selectedType === "all"
      ? true
      : deck.deckType.toLowerCase() === selectedType.toLowerCase();
      
    const matchesMembership = selectedFilter === "all"
      ? true
      : deck.membership === selectedFilter;
      
    const matchesTab = deckMatchesTab(deck, activeTab);

    return matchesSearch && matchesType && matchesMembership && matchesTab;
  }) || [];
};